import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCustomerComprehensiveReport,
  exportCustomerReportExcel,
  exportCustomerReportPdf,
  getClients,
  getOrderStatuses
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Users,
  FileText,
  CreditCard,
  Wallet,
  ClipboardList,
  Download,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Building2,
  User,
  Phone,
  Mail,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  FileSpreadsheet,
  FileDown,
  Search,
  X,
  Loader2,
  ExternalLink,
  UserCircle,
  Briefcase,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, cn } from '@/lib/utils';

const INVOICE_STATUSES = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Sent', label: 'Sent' },
  { value: 'Partially Paid', label: 'Partially Paid' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Overdue', label: 'Overdue' },
  { value: 'Cancelled', label: 'Cancelled' },
];

// Stat Card Component
function StatCard({ title, value, icon: Icon, trend, trendValue, color = 'primary', loading }) {
  const colorClasses = {
    primary: 'from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]',
    success: 'from-emerald-500 to-green-500',
    warning: 'from-amber-500 to-orange-500',
    danger: 'from-red-500 to-rose-500',
    info: 'from-blue-500 to-cyan-500',
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden" data-testid="stat-card-loading">
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="relative overflow-hidden group hover:shadow-lg transition-all duration-300"
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={cn(
        'absolute inset-x-0 top-0 h-1 bg-gradient-to-r',
        colorClasses[color]
      )} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1" data-testid={`stat-value-${title.toLowerCase().replace(/\s+/g, '-')}`}>
              {value}
            </p>
            {trend && trendValue && (
              <div className={cn(
                'flex items-center gap-1 mt-2 text-xs font-medium',
                trend === 'up' ? 'text-emerald-600' : 'text-red-500'
              )}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className={cn(
            'p-3 rounded-xl bg-gradient-to-br',
            colorClasses[color],
            'text-white shadow-lg group-hover:scale-105 transition-transform'
          )}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Client Detail Section Component
function ClientDetailSection({ client, expandedClient, setExpandedClient }) {
  const isExpanded = expandedClient === client.client_id;
  const navigate = useNavigate();

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Paid': 'bg-green-100 text-green-700 ring-1 ring-green-300',
      'Partially Paid': 'bg-amber-100 text-amber-700 ring-1 ring-amber-300',
      'Overdue': 'bg-red-100 text-red-700 ring-1 ring-red-300',
      'Sent': 'bg-blue-100 text-blue-700 ring-1 ring-blue-300',
      'Draft': 'bg-gray-100 text-gray-700 ring-1 ring-gray-300',
      'Completed': 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300',
      'In Progress': 'bg-purple-100 text-purple-700 ring-1 ring-purple-300',
      'New': 'bg-sky-100 text-sky-700 ring-1 ring-sky-300',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700 ring-1 ring-slate-300';
  };

  return (
    <Card 
      className={cn(
        'transition-all duration-300',
        isExpanded ? 'ring-2 ring-primary/30 shadow-lg' : 'hover:shadow-md'
      )}
      data-testid={`client-card-${client.client_id}`}
    >
      <Collapsible open={isExpanded} onOpenChange={() => setExpandedClient(isExpanded ? null : client.client_id)}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-br from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] text-white shadow-md">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    {client.client_name}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/clients/${client.client_id}`);
                      }}
                      data-testid={`view-client-${client.client_id}`}
                    >
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </CardTitle>
                  <CardDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    {client.trade_license && (
                      <span className="flex items-center gap-1 text-xs">
                        <FileText className="w-3 h-3" /> {client.trade_license}
                      </span>
                    )}
                    {client.phone && (
                      <span className="flex items-center gap-1 text-xs">
                        <Phone className="w-3 h-3" /> {client.phone}
                      </span>
                    )}
                    {client.email && (
                      <span className="flex items-center gap-1 text-xs">
                        <Mail className="w-3 h-3" /> {client.email}
                      </span>
                    )}
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="px-3">
                    <p className="text-xs text-muted-foreground">Invoiced</p>
                    <p className="font-semibold text-sm">{formatCurrency(client.metrics.total_invoiced)}</p>
                  </div>
                  <div className="px-3">
                    <p className="text-xs text-muted-foreground">Paid</p>
                    <p className="font-semibold text-sm text-emerald-600">{formatCurrency(client.metrics.total_paid)}</p>
                  </div>
                  <div className="px-3">
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className="font-semibold text-sm text-amber-600">{formatCurrency(client.metrics.total_outstanding)}</p>
                  </div>
                  <div className="px-3">
                    <p className="text-xs text-muted-foreground">Wallet</p>
                    <p className="font-semibold text-sm text-blue-600">{formatCurrency(client.metrics.wallet_balance)}</p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <Tabs defaultValue="invoices" className="w-full">
              <TabsList className="grid grid-cols-6 w-full max-w-3xl mb-4">
                <TabsTrigger value="invoices" className="gap-1" data-testid="tab-invoices">
                  <FileText className="w-4 h-4" />
                  Invoices ({client.invoices?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="orders" className="gap-1" data-testid="tab-orders">
                  <ClipboardList className="w-4 h-4" />
                  Orders ({client.service_orders?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="services" className="gap-1" data-testid="tab-services">
                  <Briefcase className="w-4 h-4" />
                  Services ({client.services?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="payments" className="gap-1" data-testid="tab-payments">
                  <CreditCard className="w-4 h-4" />
                  Payments ({client.payments?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="workers" className="gap-1" data-testid="tab-workers">
                  <Users className="w-4 h-4" />
                  Workers ({client.workers?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="wallet" className="gap-1" data-testid="tab-wallet">
                  <Wallet className="w-4 h-4" />
                  Wallet ({client.wallet_transactions?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Invoices Tab */}
              <TabsContent value="invoices">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.invoices?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No invoices found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.invoices?.map((inv) => (
                          <TableRow key={inv.id} className="hover:bg-muted/30">
                            <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                            <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                            <TableCell>{formatDate(inv.due_date)}</TableCell>
                            <TableCell>
                              <Badge className={cn('font-medium', getStatusBadge(inv.status))}>
                                {inv.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(inv.total_amount)}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(inv.amount_paid)}</TableCell>
                            <TableCell className="text-right font-mono text-amber-600">{formatCurrency(inv.balance_due)}</TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/invoices/${inv.id}`)}
                                data-testid={`view-invoice-${inv.id}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Service Orders Tab */}
              <TabsContent value="orders">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Order #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Services</TableHead>
                        <TableHead className="text-right">Govt Fees</TableHead>
                        <TableHead className="text-right">Service Fees</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.service_orders?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No service orders found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.service_orders?.map((order) => (
                          <TableRow key={order.id} className="hover:bg-muted/30">
                            <TableCell className="font-medium">{order.order_number}</TableCell>
                            <TableCell>{formatDate(order.created_at)}</TableCell>
                            <TableCell>
                              <Badge className={cn('font-medium', getStatusBadge(order.status))}>
                                {order.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{order.items?.length || 0}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(order.govt_fee || 0)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(order.service_fee || 0)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(order.grand_total || 0)}</TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/service-orders/${order.id}`)}
                                data-testid={`view-order-${order.id}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Services Detail Tab */}
              <TabsContent value="services">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Order #</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned Workers</TableHead>
                        <TableHead className="text-right">Govt Fee</TableHead>
                        <TableHead className="text-right">Service Fee</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.services?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            No services found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.services?.map((svc, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/30">
                            <TableCell className="font-medium">{svc.order_number}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{svc.service_name}</p>
                                <p className="text-xs text-muted-foreground">{svc.service_code}</p>
                              </div>
                            </TableCell>
                            <TableCell>{svc.quantity}</TableCell>
                            <TableCell>
                              <Badge className={cn('font-medium', getStatusBadge(svc.status))}>
                                {svc.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {svc.assigned_workers?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {svc.assigned_workers.map((w, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {w.worker_name} ({w.quantity})
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">Unassigned</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(svc.govt_fee)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(svc.service_fee)}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(svc.total)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Payment #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.payments?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No payments found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.payments?.map((pay) => (
                          <TableRow key={pay.id} className="hover:bg-muted/30">
                            <TableCell className="font-medium">{pay.payment_number || pay.receipt_number}</TableCell>
                            <TableCell>{formatDate(pay.payment_date)}</TableCell>
                            <TableCell>{pay.payment_method}</TableCell>
                            <TableCell>{pay.invoice_number || '-'}</TableCell>
                            <TableCell>{pay.reference_number || '-'}</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600 font-semibold">
                              {formatCurrency(pay.amount)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Workers Tab */}
              <TabsContent value="workers">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Worker Name</TableHead>
                        <TableHead>Nationality</TableHead>
                        <TableHead>Visa Status</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.workers?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                            No workers found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.workers?.map((worker) => (
                          <TableRow key={worker.id} className="hover:bg-muted/30">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <UserCircle className="w-5 h-5 text-muted-foreground" />
                                {worker.name}
                              </div>
                            </TableCell>
                            <TableCell>{worker.nationality || '-'}</TableCell>
                            <TableCell>
                              {worker.visa_status && (
                                <Badge variant="outline">{worker.visa_status}</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => navigate(`/workers/${worker.id}`)}
                                data-testid={`view-worker-${worker.id}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Wallet Transactions Tab */}
              <TabsContent value="wallet">
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {client.wallet_transactions?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            No wallet transactions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        client.wallet_transactions?.map((txn, idx) => (
                          <TableRow key={idx} className="hover:bg-muted/30">
                            <TableCell>{formatDate(txn.created_at)}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={txn.transaction_type === 'credit' ? 'default' : 'secondary'}
                                className={txn.transaction_type === 'credit' 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-amber-100 text-amber-700'
                                }
                              >
                                {txn.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell>{txn.description || '-'}</TableCell>
                            <TableCell>{txn.reference || '-'}</TableCell>
                            <TableCell className={cn(
                              'text-right font-mono font-semibold',
                              txn.transaction_type === 'credit' ? 'text-emerald-600' : 'text-amber-600'
                            )}>
                              {txn.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(txn.amount))}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {formatCurrency(txn.balance_after || 0)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function CustomerReportsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [clients, setClients] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    client_id: 'all',
    start_date: '',
    end_date: '',
    invoice_status: 'all',
    order_status: 'all',
  });
  
  // Expanded client
  const [expandedClient, setExpandedClient] = useState(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [clientsRes, statusesRes] = await Promise.all([
        getClients().catch(() => ({ data: { items: [] } })),
        getOrderStatuses().catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data?.items || clientsRes.data || []);
      setOrderStatuses(statusesRes.data || []);
      await loadReport();
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load initial data');
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.client_id && filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.invoice_status && filters.invoice_status !== 'all') params.invoice_status = filters.invoice_status;
      if (filters.order_status && filters.order_status !== 'all') params.order_status = filters.order_status;
      
      const response = await getCustomerComprehensiveReport(params);
      setReportData(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load customer report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.client_id && filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await exportCustomerReportExcel(params);
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Customer_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel report downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel report');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.client_id && filters.client_id !== 'all') params.client_id = filters.client_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await exportCustomerReportPdf(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Customer_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF report');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    loadReport();
  };

  const handleClearFilters = () => {
    setFilters({
      client_id: 'all',
      start_date: '',
      end_date: '',
      invoice_status: 'all',
      order_status: 'all',
    });
    setSearchQuery('');
  };

  // Filter clients by search query
  const filteredClients = useMemo(() => {
    if (!reportData?.clients) return [];
    if (!searchQuery) return reportData.clients;
    
    const query = searchQuery.toLowerCase();
    return reportData.clients.filter(client => 
      client.client_name?.toLowerCase().includes(query) ||
      client.trade_license?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.includes(query)
    );
  }, [reportData?.clients, searchQuery]);

  const summary = reportData?.summary || {};

  return (
    <div className="space-y-6" data-testid="customer-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Customer Comprehensive Report
          </h1>
          <p className="text-muted-foreground mt-1">
            360° view of all customer data including invoices, orders, payments, and more
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleExportExcel}
            disabled={exporting || loading}
            className="gap-2"
            data-testid="export-excel-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Export Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportPdf}
            disabled={exporting || loading}
            className="gap-2"
            data-testid="export-pdf-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Export PDF
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <Card data-testid="filters-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-muted-foreground" />
              <CardTitle className="text-lg">Smart Filters</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClearFilters}
              className="gap-1 text-muted-foreground hover:text-foreground"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" />
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Client Select */}
            <div className="space-y-2">
              <Label htmlFor="client">Client</Label>
              <Select 
                value={filters.client_id} 
                onValueChange={(v) => handleFilterChange('client_id', v)}
              >
                <SelectTrigger id="client" data-testid="filter-client">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.trade_name || c.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Range */}
            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                data-testid="filter-end-date"
              />
            </div>

            {/* Invoice Status */}
            <div className="space-y-2">
              <Label htmlFor="invoice_status">Invoice Status</Label>
              <Select 
                value={filters.invoice_status} 
                onValueChange={(v) => handleFilterChange('invoice_status', v)}
              >
                <SelectTrigger id="invoice_status" data-testid="filter-invoice-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {INVOICE_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Order Status */}
            <div className="space-y-2">
              <Label htmlFor="order_status">Order Status</Label>
              <Select 
                value={filters.order_status} 
                onValueChange={(v) => handleFilterChange('order_status', v)}
              >
                <SelectTrigger id="order_status" data-testid="filter-order-status">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {orderStatuses.map(s => (
                    <SelectItem key={s.id || s.code} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button 
              onClick={handleApplyFilters}
              disabled={loading}
              className="gap-2 bg-gradient-to-r from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)] hover:brightness-105"
              data-testid="apply-filters-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard 
          title="Total Clients" 
          value={loading ? '...' : summary.total_clients || 0}
          icon={Building2}
          color="primary"
          loading={loading}
        />
        <StatCard 
          title="Total Invoiced" 
          value={loading ? '...' : formatCurrency(summary.total_invoice_amount || 0)}
          icon={FileText}
          color="info"
          loading={loading}
        />
        <StatCard 
          title="Total Collected" 
          value={loading ? '...' : formatCurrency(summary.total_paid || 0)}
          icon={DollarSign}
          color="success"
          loading={loading}
        />
        <StatCard 
          title="Outstanding" 
          value={loading ? '...' : formatCurrency(summary.total_outstanding || 0)}
          icon={Receipt}
          color="warning"
          loading={loading}
        />
        <StatCard 
          title="Wallet Balance" 
          value={loading ? '...' : formatCurrency(summary.total_wallet_balance || 0)}
          icon={Wallet}
          color="info"
          loading={loading}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="stat-invoices">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Invoices</p>
              <p className="text-xl font-semibold">{loading ? '...' : summary.total_invoices || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-orders">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <ClipboardList className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Service Orders</p>
              <p className="text-xl font-semibold">{loading ? '...' : summary.total_orders || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-services">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100">
              <Briefcase className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Services</p>
              <p className="text-xl font-semibold">{loading ? '...' : summary.total_services || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-testid="stat-workers">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Workers</p>
              <p className="text-xl font-semibold">{loading ? '...' : summary.total_workers || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search clients by name, license, email, or phone..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-clients"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={() => setSearchQuery('')}
            data-testid="clear-search-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Client List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            Clients ({filteredClients.length})
          </h2>
          {searchQuery && (
            <p className="text-sm text-muted-foreground">
              Showing {filteredClients.length} of {reportData?.clients?.length || 0} clients
            </p>
          )}
        </div>
        
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="p-6">
                <div className="flex items-center gap-4">
                  <Skeleton className="w-14 h-14 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-64" />
                  </div>
                  <Skeleton className="h-10 w-32" />
                </div>
              </Card>
            ))}
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="py-12">
            <div className="text-center text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No clients found</p>
              <p className="text-sm mt-1">
                {searchQuery 
                  ? 'Try adjusting your search query'
                  : 'Apply filters to see customer data'
                }
              </p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredClients.map(client => (
              <ClientDetailSection 
                key={client.client_id}
                client={client}
                expandedClient={expandedClient}
                setExpandedClient={setExpandedClient}
              />
            ))}
          </div>
        )}
      </div>

      {/* Report Generated Info */}
      {reportData?.generated_at && (
        <p className="text-center text-xs text-muted-foreground pt-4">
          Report generated at {formatDate(reportData.generated_at, true)}
        </p>
      )}
    </div>
  );
}
